import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Tile from 'components/TileLegacy';
import { Tile as TileV2, LIVE_TILE } from 'componentsV2/Tile';
import { isUpcomingContent } from 'helpers/ContentHelper';
import { FreemiumContext } from 'index/FreemiumProvider';

const CarouselItem = React.memo(
  ({
    applyInlineBackground,
    areBadgesDisabled,
    breakpoint,
    collectionType,
    favoritesManager,
    hasAccountOrProvider,
    item,
    itemIndex,
    isFreemium,
    isHoverStateV2,
    isLiveTileV2,
    isPersonality,
    isSeriesDetailPage,
    isSeasons,
    headline,
    panelCount,
    processed,
    tileTheme,
    disableHoverStateActionButtons,
    renderImage,
    scaleFromLeft,
    scaleFromRight,
    searchCompleted,
    setFreemiumModalVisibility,
    setSearchCompleted,
    searchTerm,
    segmentPlaylistButtonAction,
    shouldDisplayNetworkLogo,
    shouldInvertTitleOrder,
    showUpgradeCTA,
    subtitleLines,
    truncateSubtitle,
    onTileClick,
    upcomingOverlay,
  }) => {
    if (!item || item.length === 0 || !processed) return null;

    const upcomingOverlayClick = isUpcomingContent(item) ? upcomingOverlay : null;
    const { id } = processed;
    const key = `${id}-${itemIndex}`;
    const callSign = item?.callSign;

    const tileProps = {
      ...processed,
      applyInlineBackground,
      breakpoint,
      callSign,
      collectionType,
      customTileClick: upcomingOverlayClick,
      'data-test': 'carousel-tile',
      disableHoverStateActionButtons,
      favoritesManager,
      hasAccountOrProvider,
      headline,
      isFreemium,
      isPersonality,
      isSeasons,
      isSeriesDetailPage,
      itemIndex,
      key,
      panelCount,
      onTileClick,
      areBadgesDisabled,
      renderImage,
      scaleFromLeft,
      scaleFromRight,
      searchCompleted,
      setSearchCompleted,
      searchTerm,
      segmentPlaylistButtonAction,
      setFreemiumModalVisibility,
      shouldDisplayNetworkLogo,
      shouldInvertTitleOrder,
      showUpgradeCTA,
      subtitleLines,
      theme: tileTheme,
      tileImageIndex: key,
      truncateSubtitle,
    };

    return isLiveTileV2 && id !== 'seeMore' ? (
      <TileV2 {...tileProps} type={LIVE_TILE} />
    ) : (
      <Tile {...tileProps} isHoverStateV2={isHoverStateV2} isLiveTileV2={isLiveTileV2} />
    );
  },
);

const CarouselItemWrapper = props => {
  const { isFreemium, hasAccountOrProvider, setFreemiumModalVisibility } = useContext(
    FreemiumContext,
  );
  return (
    <CarouselItem
      {...props}
      isFreemium={isFreemium}
      hasAccountOrProvider={hasAccountOrProvider}
      setFreemiumModalVisibility={setFreemiumModalVisibility}
    />
  );
};

CarouselItem.defaultProps = {
  applyInlineBackground: false,
  areBadgesDisabled: false,
  breakpoint: '',
  collectionType: null,
  favoritesManager: null,
  hasAccountOrProvider: false,
  headline: {},
  item: null,
  itemIndex: 0,
  isFreemium: false,
  isHoverStateV2: false,
  isLiveTileV2: false,
  isSeriesDetailPage: false,
  disableHoverStateActionButtons: false,
  panelCount: 0,
  processed: {},
  renderImage: () => {},
  scaleFromLeft: false,
  scaleFromRight: false,
  searchCompleted: false,
  setFreemiumModalVisibility: () => {},
  setSearchCompleted: () => {},
  searchTerm: '',
  segmentPlaylistButtonAction: () => {},
  shouldDisplayNetworkLogo: false,
  shouldInvertTitleOrder: () => {},
  slideIndex: null,
  onTileClick: () => {},
  tileTheme: {},
  upcomingOverlay: () => {},
};

CarouselItem.propTypes = {
  applyInlineBackground: PropTypes.bool,
  areBadgesDisabled: PropTypes.bool,
  breakpoint: PropTypes.string,
  collectionType: PropTypes.string,
  favoritesManager: PropTypes.shape({}),
  headline: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  hasAccountOrProvider: PropTypes.bool,
  item: PropTypes.shape(),
  itemIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isFreemium: PropTypes.bool,
  isHoverStateV2: PropTypes.bool,
  isLiveTileV2: PropTypes.bool,
  isPersonality: PropTypes.bool,
  isSeriesDetailPage: PropTypes.bool,
  isSeasons: PropTypes.bool,
  disableHoverStateActionButtons: PropTypes.bool,
  panelCount: PropTypes.number,
  processed: PropTypes.shape({}),
  renderImage: PropTypes.func,
  scaleFromLeft: PropTypes.bool,
  scaleFromRight: PropTypes.bool,
  searchCompleted: PropTypes.bool,
  setFreemiumModalVisibility: PropTypes.func,
  setSearchCompleted: PropTypes.func,
  searchTerm: PropTypes.string,
  segmentPlaylistButtonAction: PropTypes.func,
  shouldDisplayNetworkLogo: PropTypes.bool,
  shouldInvertTitleOrder: PropTypes.func,
  showUpgradeCTA: PropTypes.bool,
  slideIndex: PropTypes.number,
  subtitleLines: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  truncateSubtitle: PropTypes.bool,
  onTileClick: PropTypes.func,
  tileTheme: PropTypes.shape(),
  upcomingOverlay: PropTypes.func,
};

export default React.memo(CarouselItemWrapper);
